import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import querystring from 'query-string'

import { component as spContext } from '@cred/react-analytics/contexts'
import { SnowplowContext } from '@cred/react-analytics/SnowplowContextManager'
import { logger } from '@cred/fe-logger'
import { getWidgetAppIframeId } from '@cred/widget-sdk-client'

import ViewportAnalytics from 'components/viewport-analytics'
import ViewportController from 'components/viewport-controller'
import WidgetContext from 'components/widget-context'
import InitialValuesContext from 'components/initial-values-context'
import { getMetaQuery } from 'utils/data'

import { getContexts } from './utils'

const WidgetPage = (WrappedComponent) => (props) => (
  <HOC {...props} WrappedComponent={WrappedComponent} />
)

const HOC = (props) => {
  const { WrappedComponent } = props
  const { initialValues } = useContext(InitialValuesContext)
  const widgetContext = useContext(WidgetContext)

  const {
    options: {
      ui: { theme },
      product: { variation, type, marketplace, customCTA },
      meta: {
        contentId,
        articleTitle,
        articleTags,
        segmentId,
        campaignCode,
        contentDistributor,
        contentCategory,
        campaignCodeExt,
      } = {},
      analytics,
      ...options
    },
    parentPage: { partnerReferrer, partnerDomain, partnerURL },
    credclid,
    credsessid,
    renderId,
    source = analytics ? analytics.source : null,
    campaignFromPageUrl,
  } = widgetContext

  const finalSource = source || partnerDomain || 'undefined'
  let url = ''

  if (partnerURL) {
    ;[url] = partnerURL.split('?')
  }

  const metaParams = {
    articleTitle,
    articleTags,
    contentId,
    contentCategory,
    contentDistributor,
    campaignCode,
    campaignCodeExt,
    pageUrl: partnerURL,
    segmentId,
  }
  const utms = {
    utm_campaign: campaignFromPageUrl || url,
    utm_medium: 'partner_integration',
    utm_source: finalSource,
    utm_content: options.uniqueId || '',
    credclid,
    credsessid,
    renderId,
  }
  const widgetId = getWidgetAppIframeId(logger)

  const showRateTableAsFirstPrequalStep = type === 'rate-table-with-embedded-prequal'
  const metaQuery = getMetaQuery({ ...metaParams, marketplace, type }, theme)

  const embeddedWidgetQueryParams = `${querystring.stringify({
    ...utms,
    theme,
    type,
    widget_id: widgetId,
  })}${metaQuery}`

  return (
    <ViewportController>
      {({ size, position }) => (
        <SnowplowContext
          contexts={getContexts({
            source: finalSource,
            partnerUrl: url,
            partnerReferrer,
            variation,
            type,
            marketplace,
            size,
            position,
            credclid,
            credsessid,
            renderId,
            contentId,
            articleTitle,
            articleTags,
            segmentId,
            campaignCode,
            contentDistributor,
            contentCategory,
            campaignCodeExt,
          })}
        >
          <ViewportAnalytics size={size}>
            <WrappedComponent
              {...props}
              key={`${marketplace}#${type}`}
              theme={theme}
              variation={variation}
              customCTA={customCTA}
              uniqueId={options.uniqueId}
              widgetId={widgetId}
              widgetContext={widgetContext}
              initialValues={initialValues}
              verticals={marketplace}
              type={type}
              omitUtmParams={options.omitUtmParams}
              showRateTableAsFirstPrequalStep={showRateTableAsFirstPrequalStep}
              metaParams={metaParams}
              utms={utms}
              analyticsContexts={[spContext.analytics({ name: `${marketplace}#${type}` })]}
              embeddedWidgetQueryParams={embeddedWidgetQueryParams}
            />
          </ViewportAnalytics>
        </SnowplowContext>
      )}
    </ViewportController>
  )
}

HOC.propTypes = {
  WrappedComponent: PropTypes.elementType.isRequired,
}

export default WidgetPage
